/**
 * Global chunk loading error handler
 * Handles ChunkLoadError events and provides automatic recovery mechanisms
 */

import { store } from 'core/store/configureStore';
import { errorRequest } from 'core/services/product';

export interface ChunkErrorConfig {
  maxRetries: number;
  retryDelay: number;
  enableCacheBusting: boolean;
  enableAutoReload: boolean;
  autoReloadDelay: number;
}

const defaultConfig: ChunkErrorConfig = {
  maxRetries: 3,
  retryDelay: 2000,
  enableCacheBusting: true,
  enableAutoReload: true,
  autoReloadDelay: 10000, // 10 seconds
};

class ChunkErrorHandler {
  private config: ChunkErrorConfig;
  private errorCounts = new Map<string, number>();
  private retryTimeouts = new Set<NodeJS.Timeout>();
  private isHandlerRegistered = false;

  constructor(config: Partial<ChunkErrorConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  init() {
    if (this.isHandlerRegistered || typeof window === 'undefined') {
      return;
    }

    // Register global error handler
    window.addEventListener('error', this.handleGlobalError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);

    // Register webpack chunk loading error handler if available
    if ((window as any).__webpack_require__) {
      this.registerWebpackErrorHandler();
    }

    this.isHandlerRegistered = true;
    console.log('Chunk error handler initialized');
  }

  cleanup() {
    if (!this.isHandlerRegistered) {
      return;
    }

    window.removeEventListener('error', this.handleGlobalError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);

    // Clear any pending timeouts
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout));
    this.retryTimeouts.clear();

    this.isHandlerRegistered = false;
  }

  private handleGlobalError = (event: ErrorEvent) => {
    if (this.isChunkError(event.error)) {
      event.preventDefault(); // Prevent default error handling
      this.handleChunkError(event.error, 'window.error');
    }
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (this.isChunkError(event.reason)) {
      event.preventDefault(); // Prevent default rejection handling
      this.handleChunkError(event.reason, 'unhandled.rejection');
    }
  };

  private registerWebpackErrorHandler() {
    try {
      const webpackRequire = (window as any).__webpack_require__;
      if (webpackRequire && webpackRequire.f && webpackRequire.f.j) {
        const originalLoader = webpackRequire.f.j;

        webpackRequire.f.j = (chunkId: string, promises: Promise<any>[]) => {
          // Call original loader
          originalLoader(chunkId, promises);

          // Wrap all promises to catch chunk loading errors
          const wrappedPromises = promises.map(promise =>
            promise.catch(error => {
              if (this.isChunkError(error)) {
                this.handleChunkError(error, `webpack.chunk.${chunkId}`);
                throw error; // Re-throw to maintain original promise behavior
              }
              throw error;
            }),
          );

          // Replace original promises with wrapped ones
          promises.splice(0, promises.length, ...wrappedPromises);
        };
      }
    } catch (error) {
      console.warn('Failed to register webpack error handler:', error);
    }
  }

  private isChunkError(error: any): boolean {
    if (!error) return false;

    const errorMessage = error.message || error.toString();
    const errorName = error.name;

    return (
      errorName === 'ChunkLoadError' ||
      errorMessage.includes('Loading chunk') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('failed') ||
      (errorMessage.includes('chunk') &&
        (errorMessage.includes('load') || errorMessage.includes('fetch') || errorMessage.includes('network')))
    );
  }

  private handleChunkError(error: any, source: string) {
    const errorKey = `${source}:${error.message}`;
    const currentCount = this.errorCounts.get(errorKey) || 0;

    console.warn(`Chunk error detected (${source}):`, error.message);

    // Report error to backend
    store.dispatch(
      errorRequest({
        code: 3,
        message: `Chunk loading error (${source}): ${error.message}`,
        stackTrace: error.stack || JSON.stringify({ source, error: error.toString() }),
      }),
    );

    if (currentCount >= this.config.maxRetries) {
      console.error(`Max retries exceeded for chunk error: ${errorKey}`);
      this.handleFinalFailure(error);
      return;
    }

    this.errorCounts.set(errorKey, currentCount + 1);
    this.scheduleRetry(error, source, currentCount);
  }

  private scheduleRetry(error: any, source: string, attempt: number) {
    const delay = this.config.retryDelay * Math.pow(2, attempt); // Exponential backoff

    console.log(`Scheduling chunk error retry in ${delay}ms (attempt ${attempt + 1}/${this.config.maxRetries})`);

    const timeout = setTimeout(() => {
      this.retryTimeouts.delete(timeout);
      this.performRetry(error, source, attempt);
    }, delay);

    this.retryTimeouts.add(timeout);
  }

  private performRetry(error: any, source: string, attempt: number) {
    console.log(`Performing chunk error retry (attempt ${attempt + 1})`);

    if (this.config.enableCacheBusting) {
      this.clearCaches();
    }

    // For the final retry attempt, force a page reload
    if (attempt === this.config.maxRetries - 1 && this.config.enableAutoReload) {
      this.scheduleAutoReload();
    }
  }

  private clearCaches() {
    try {
      // Clear webpack module cache
      if ((window as any).__webpack_require__?.cache) {
        const cache = (window as any).__webpack_require__.cache;
        Object.keys(cache).forEach(key => {
          if (key.includes('chunk')) {
            delete cache[key];
          }
        });
      }

      // Clear browser caches
      if ('caches' in window) {
        caches
          .keys()
          .then(cacheNames => {
            cacheNames.forEach(cacheName => {
              if (cacheName.includes('js') || cacheName.includes('chunk')) {
                caches.delete(cacheName).catch(console.warn);
              }
            });
          })
          .catch(console.warn);
      }

      // Clear sessionStorage and localStorage related to chunks
      try {
        Object.keys(sessionStorage).forEach(key => {
          if (key.includes('chunk') || key.includes('webpack')) {
            sessionStorage.removeItem(key);
          }
        });

        Object.keys(localStorage).forEach(key => {
          if (key.includes('chunk') || key.includes('webpack')) {
            localStorage.removeItem(key);
          }
        });
      } catch (storageError) {
        console.warn('Failed to clear storage:', storageError);
      }
    } catch (cacheError) {
      console.warn('Failed to clear caches:', cacheError);
    }
  }

  private scheduleAutoReload() {
    if (!this.config.enableAutoReload) {
      return;
    }

    console.warn(`Scheduling automatic page reload in ${this.config.autoReloadDelay}ms due to chunk errors`);

    const timeout = setTimeout(() => {
      this.retryTimeouts.delete(timeout);
      this.forceReload();
    }, this.config.autoReloadDelay);

    this.retryTimeouts.add(timeout);

    // Show user notification
    this.showReloadNotification();
  }

  private showReloadNotification() {
    // Only show if we're not already showing an error boundary
    if (document.querySelector('[data-chunk-error-boundary]')) {
      return;
    }

    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-yellow-500 text-white p-4 rounded-lg shadow-lg z-50';
    notification.innerHTML = `
      <div class="flex items-center space-x-3">
        <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
        <div>
          <p class="font-semibold">Loading Issues Detected</p>
          <p class="text-sm">Page will reload automatically to fix the issue...</p>
        </div>
      </div>
    `;

    document.body.appendChild(notification);

    // Auto-remove notification after delay
    setTimeout(() => {
      notification.remove();
    }, this.config.autoReloadDelay - 1000);
  }

  private handleFinalFailure(error: any) {
    console.error('Final failure for chunk loading, showing error boundary');

    // Force reload after a short delay
    if (this.config.enableAutoReload) {
      setTimeout(() => {
        this.forceReload();
      }, 5000);
    }
  }

  private forceReload() {
    try {
      // Clear all caches before reload
      if ('caches' in window) {
        caches
          .keys()
          .then(cacheNames => {
            Promise.all(cacheNames.map(name => caches.delete(name)))
              .then(() => {
                this.performReload();
              })
              .catch(() => {
                this.performReload();
              });
          })
          .catch(() => {
            this.performReload();
          });
      } else {
        this.performReload();
      }
    } catch (reloadError) {
      console.error('Failed to force reload:', reloadError);
      this.performReload();
    }
  }

  private performReload() {
    const url = new URL((window as any).location.href);
    url.searchParams.set('_cb', Date.now().toString());
    (window as any).location.href = url.toString();
  }

  // Public methods for manual control
  public setConfig(config: Partial<ChunkErrorConfig>) {
    this.config = { ...this.config, ...config };
  }

  public clearErrorCounts() {
    this.errorCounts.clear();
  }

  public getErrorCounts() {
    return new Map(this.errorCounts);
  }
}

// Export singleton instance
export const chunkErrorHandler = new ChunkErrorHandler();

// Auto-initialize in browser environment
if (typeof window !== 'undefined') {
  // Initialize after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => chunkErrorHandler.init());
  } else {
    chunkErrorHandler.init();
  }
}
